/**
 * OwlCarouselScrollbar
 *
 * Attaches a custom draggable scrollbar to an Owl Carousel instance.
 * The thumb reflects the current slide position and can be dragged or
 * clicked to navigate the carousel.
 *
 * Supports both standard (items-based) and autoWidth carousels.
 *
 * Usage:
 *   const scrollbar = new OwlCarouselScrollbar($carousel, trackEl, thumbEl);
 *   scrollbar.init(); // Call BEFORE owlCarousel() to catch the initialized event
 *
 * @param {jQuery}  $carousel - Owl Carousel jQuery element
 * @param {Element} trackEl   - Scrollbar track DOM element
 * @param {Element} thumbEl   - Scrollbar thumb DOM element
 */
class OwlCarouselScrollbar {
  #carousel;
  #track;
  #thumb;

  #state = { totalItems: 0, currentIndex: 0, visibleItems: 1 };
  #owl = null;

  #isDragging = false;
  #dragStartX = 0;
  #dragStartLeft = 0;

  constructor($carousel, trackEl, thumbEl) {
    this.#carousel = $carousel;
    this.#track = trackEl;
    this.#thumb = thumbEl;
  }

  init() {
    this.#carousel.on(
      'initialized.owl.carousel translated.owl.carousel resized.owl.carousel',
      this.#onCarouselUpdate.bind(this)
    );

    this.#thumb.addEventListener('pointerdown', this.#onPointerDown.bind(this));
    this.#thumb.addEventListener('pointermove', this.#onPointerMove.bind(this));
    this.#thumb.addEventListener('pointerup', this.#onPointerUp.bind(this));

    this.#thumb.addEventListener('pointercancel', this.#onPointerUp.bind(this));
    this.#thumb.addEventListener('lostpointercapture', this.#onPointerUp.bind(this));
    this.#track.addEventListener('click', this.#onTrackClick.bind(this));
  }

  destroy() {
    this.#carousel.off(
      'initialized.owl.carousel translated.owl.carousel resized.owl.carousel'
    );
  }

  #onCarouselUpdate(event) {
    const owl = event.relatedTarget;
    if (!owl || !owl.settings) return;

    this.#owl = owl;

    const totalItems = event.item && event.item.count > 0
      ? event.item.count
      : owl._items.length;

    const currentIndex = event.item ? event.item.index : 0;

    const visibleItems = event.page && event.page.size > 0
      ? event.page.size
      : owl.settings.items || 1;

    this.#state = { totalItems, currentIndex, visibleItems };

    if (!this.#isDragging) {
      this.#updateThumb();
    }
  }

  #updateThumb() {
    const trackWidth = this.#track.offsetWidth;
    if (trackWidth === 0) return;

    let thumbWidth, thumbLeft;

    if (this.#owl && this.#owl.settings && this.#owl.settings.autoWidth) {
      const stageOuter = this.#carousel[0].querySelector('.owl-stage-outer');
      const stage = this.#carousel[0].querySelector('.owl-stage');
      if (!stageOuter || !stage) return;

      const visibleWidth = stageOuter.offsetWidth;
      if (visibleWidth <= 0) return;

      const maxScroll = Math.abs(this.#owl.coordinates(this.#owl.maximum()) || 0);

      const totalRange = visibleWidth + maxScroll;
      thumbWidth = totalRange > 0 ? Math.round((visibleWidth / totalRange) * trackWidth) : trackWidth;

      const scrolled = this.#getStageScrolled(stage);
      const maxLeft = trackWidth - thumbWidth;
      thumbLeft = maxScroll > 0 ? Math.round((scrolled / maxScroll) * maxLeft) : 0;
    } else {
      const { totalItems, currentIndex, visibleItems } = this.#state;
      if (totalItems === 0) return;

      const scrollable = Math.max(totalItems - visibleItems, 0);
      thumbWidth = scrollable === 0
        ? trackWidth
        : Math.round((visibleItems / totalItems) * trackWidth);

      const maxLeft = trackWidth - thumbWidth;
      thumbLeft = scrollable === 0
        ? 0
        : Math.round((currentIndex / scrollable) * maxLeft);
    }

    this.#thumb.style.width = `${thumbWidth}px`;
    this.#thumb.style.transform = `translateX(${thumbLeft}px)`;
  }

  #getStageScrolled(stageEl) {
    const transform = stageEl.style.transform
      || window.getComputedStyle(stageEl).transform;
    if (transform && transform !== 'none') {
      const match = transform.match(/translate(?:3d|X)?\(([^,)]+)/);
      return match ? Math.abs(parseFloat(match[1])) : 0;
    }
    return Math.abs(parseFloat(stageEl.style.left) || 0);
  }

  #getCurrentLeft() {
    const match = this.#thumb.style.transform.match(/translateX\(([0-9.]+)px\)/);
    return match ? parseFloat(match[1]) : 0;
  }

  #onPointerDown(e) {
    e.preventDefault();
    this.#isDragging = true;
    this.#dragStartX = e.clientX;
    this.#dragStartLeft = this.#getCurrentLeft();
    this.#thumb.setPointerCapture(e.pointerId);
    this.#thumb.classList.add('is-dragging');
  }

  #onPointerMove(e) {
    if (!this.#isDragging) return;

    const trackWidth = this.#track.offsetWidth;
    const thumbWidth = parseFloat(this.#thumb.style.width) || 0;
    const maxLeft = trackWidth - thumbWidth;
    if (maxLeft <= 0) return;

    const delta = e.clientX - this.#dragStartX;
    const newLeft = Math.min(Math.max(this.#dragStartLeft + delta, 0), maxLeft);
    this.#thumb.style.transform = `translateX(${newLeft}px)`;

    this.#carousel.trigger('to.owl.carousel', [this.#thumbToIndex(newLeft, maxLeft), 150]);
  }

  #onPointerUp() {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    this.#thumb.classList.remove('is-dragging');
    this.#updateThumb();
  }

  #onTrackClick(e) {
    if (e.target === this.#thumb) return;

    const trackWidth = this.#track.offsetWidth;
    const thumbWidth = parseFloat(this.#thumb.style.width) || 0;
    const maxLeft = trackWidth - thumbWidth;
    if (maxLeft <= 0) return;

    const rect = this.#track.getBoundingClientRect();
    const clickLeft = Math.min(Math.max((e.clientX - rect.left) - thumbWidth / 2, 0), maxLeft);

    this.#carousel.trigger('to.owl.carousel', [this.#thumbToIndex(clickLeft, maxLeft), 300]);
  }

  #thumbToIndex(thumbLeft, maxLeft) {
    const ratio = maxLeft > 0 ? thumbLeft / maxLeft : 0;

    if (this.#owl && this.#owl.settings && this.#owl.settings.autoWidth) {
      const stageOuter = this.#carousel[0].querySelector('.owl-stage-outer');
      const stage = this.#carousel[0].querySelector('.owl-stage');
      if (!stageOuter || !stage) return 0;

      const maxScroll = Math.abs(this.#owl.coordinates(this.#owl.maximum()) || 0);
      const targetScroll = ratio * maxScroll;

      const maxIdx = this.#owl.maximum();
      let best = 0;
      let minDiff = Infinity;
      for (let i = 0; i <= maxIdx; i++) {
        const diff = Math.abs(Math.abs(this.#owl.coordinates(i) || 0) - targetScroll);
        if (diff < minDiff) { minDiff = diff; best = i; }
      }
      return best;
    }

    const { totalItems, visibleItems } = this.#state;
    return Math.round(ratio * Math.max(totalItems - visibleItems, 0));
  }
}

window.OwlCarouselScrollbar = OwlCarouselScrollbar;